/**
 * Legal layer: the operator's identity, the version of each legal document and the consent contract.
 *
 * ONE source for the pages (/legal/...), the e-mail footers, the consent records and the docs.
 * Anything the operator has not published is a visible `[KITÖLTENDŐ: …]` marker – never a guess.
 *
 * Source of the operator data: https://www.imronit.hu/impresszum.html and /adatvedelem.html (same operator),
 * collected 2026-11-01. The game's VPS and the parallaxgate.com domain are at ATW Internet Kft. (see `hosting`).
 */

export const PENDING_PREFIX = '[KITÖLTENDŐ';

export const OPERATOR = {
  /** Trade name the players see. */
  brand: 'ImRon IT',
  /** Legal name of the controller. */
  legalName: 'Török Imre Attila',
  form: 'egyéni vállalkozó',
  /** Full name as it appears in imronit.hu's privacy notice. */
  fullName: 'ImRon IT – Török Imre Attila egyéni vállalkozó',
  seat: '6764 Balástya, Balástyai Tanyák 261.',
  country: 'Magyarország',
  taxNumber: '91141714-1-26',
  registrationNumber: '60542494',
  registrationNote: 'egyéni vállalkozói nyilvántartási szám',
  email: 'info@imronit.hu',
  phone: '+36 30 592 9276',
  website: 'https://www.imronit.hu',
  /**
   * Hosting provider of the game (VPS) AND registrar/DNS of parallaxgate.com: ATW Internet Kft.
   * Sources: https://atw.hu/adatvedelem and https://atw.hu/aszf (seat, company registration number, contacts, "saját
   * budapesti adatközpontok"); the tax number is published on https://www.imronit.hu/impresszum.html (ATW's own pages
   * do not show it). Collected 2026-11-01.
   */
  hosting: {
    name: 'ATW Internet Kft.',
    seat: '1138 Budapest, Esztergomi út 66. fsz. 1.',
    taxNumber: '13471868-2-41',
    companyRegistrationNumber: '01-09-736956',
    email: 'info@atw.co.hu',
    supportEmail: 'support@atw.co.hu',
    phone: '+36 1 6000 289',
    website: 'https://atw.hu',
    dataCentre: 'saját budapesti adatközpontok (Magyarország)',
  },
  /** Not in any source document: the competent court is a legal decision. */
  court: '[KITÖLTENDŐ: a jogvitákra illetékes bíróság, ha a felek külön megállapodás híján a jogszabályi főszabálytól eltérnek]',
  /** Supervisory authority, as published on imronit.hu. */
  authority: {
    name: 'Nemzeti Adatvédelmi és Információszabadság Hatóság (NAIH)',
    address: '1055 Budapest, Falk Miksa utca 9–11.',
    email: 'ugyfelszolgalat@naih.hu',
    phone: '+36 1 391 1400',
    website: 'https://naih.hu',
  },
} as const;

export const SERVICE = {
  name: 'Parallax Gate',
  domain: 'parallaxgate.com',
  url: 'https://parallaxgate.com',
} as const;

/** Minimum age to register (GDPR art. 8: Hungary applies 16 years). The decision is flagged in docs/legal/README.md. */
export const MIN_AGE = 16;

export type LegalDocKey = 'privacy' | 'terms' | 'cookies' | 'imprint';
export const LEGAL_DOC_KEYS: readonly LegalDocKey[] = ['privacy', 'terms', 'cookies', 'imprint'];

/** The documents a player must accept (the cookie notice and the imprint are information only). */
export const ACCEPTED_DOCS = ['terms', 'privacy'] as const;
export type AcceptedDoc = typeof ACCEPTED_DOCS[number];

/**
 * Version = effective date of the text. Changing a document's CONTENT without bumping its version fails the unit test
 * (`legal.test.ts`), which compares the content hash with `LEGAL_HASH_LOCK`. Bump the version, then update the lock
 * (the failing test prints the new hash).
 */
export const LEGAL_VERSIONS: Record<LegalDocKey, string> = {
  privacy: '2026-11-02',
  terms: '2026-11-02',
  cookies: '2026-11-01',
  imprint: '2026-11-02',
};

/** sha256 of the canonical JSON of each document, recorded when its version was released. */
export const LEGAL_HASH_LOCK: Record<LegalDocKey, { version: string; sha256: string }> = {
  privacy: { version: '2026-11-02', sha256: '67bfb3ca08e8be2b55c9cb8fcf4e324105560843bb97891b6d5789a9fdc221c1' },
  terms: { version: '2026-11-02', sha256: 'cc60ed3883fa0041211a3ada281f5a68fce7b44b3dd7f5760d7ced8db55fd1be' },
  cookies: { version: '2026-11-01', sha256: 'a8eabbd0892dd22d34a7fec7ea08729fa6bf8555798c1ba5299a04da3a517cf7' },
  imprint: { version: '2026-11-02', sha256: '3bb69e7e6b928a18ce2628b9a5f543eb97b70c165bd9eaf6635683dc254daafb' },
};

/** What `GET /api/public/legal` returns. */
export interface LegalVersionsResponse {
  operator: { name: string; legalName: string; email: string };
  minAge: number;
  documents: Record<LegalDocKey, { version: string; sha256: string; path: string; title: string }>;
}

export const LEGAL_PATHS: Record<LegalDocKey, string> = {
  privacy: '/legal/privacy',
  terms: '/legal/terms',
  cookies: '/legal/cookies',
  imprint: '/legal/imprint',
};

export const LEGAL_TITLES: Record<LegalDocKey, string> = {
  privacy: 'Adatkezelési tájékoztató',
  terms: 'Felhasználási feltételek',
  cookies: 'Süti- és helyi tároló tájékoztató',
  imprint: 'Impresszum',
};

/** A player's consent state, returned with the account profile. */
export interface LegalConsentState {
  /** The current versions the player has to accept. */
  current: Record<AcceptedDoc, string>;
  /** What the player accepted last (null: never). */
  accepted: { terms: string; privacy: string; at: string } | null;
  /** True when a signed-in, non-guest player has not accepted the current versions yet. */
  needsAcceptance: boolean;
}

export function isPending(s: string): boolean { return s.includes(PENDING_PREFIX); }
